import { IPhoneNumber, IPhoneNumberGroup } from '../interface'

/**
 * Extracts the area code from a phone number string
 * Handles formats like: +17194567890, 7194567890, 719-456-7890, (719) 456-7890
 */
export const extractAreaCode = (phoneNumber: string): string => {
   // Remove all non-digit characters
   const digitsOnly = phoneNumber.replace(/\D/g, '')

   // If it starts with 1 (US country code), skip it and get next 3 digits
   // Otherwise, get first 3 digits
   if (digitsOnly.startsWith('1') && digitsOnly.length >= 4) {
      return digitsOnly.substring(1, 4)
   }

   return digitsOnly.substring(0, 3)
}

/**
 * Formats a phone number to XXX-XXX-XXXX format
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
   const digitsOnly = phoneNumber?.replace(/\D/g, '') || ''

   // Remove country code if present
   const number =
      digitsOnly?.startsWith('1') && digitsOnly?.length === 11
         ? digitsOnly.substring(1)
         : digitsOnly || ''

   if (number?.length === 10) {
      return `${number?.substring(0, 3)}-${number?.substring(
         3,
         6
      )}-${number?.substring(6)}`
   }

   return phoneNumber // Return as-is if not standard format
}

/**
 * Groups phone numbers by area code
 * Returns an array of groups, each containing an area code, locality, count, and the phone numbers
 */
export const getGroupedPhoneNumbers = (
   phoneNumbers: IPhoneNumber[]
): IPhoneNumberGroup[] => {
   // Create a map to group by area code
   const groupMap = new Map<string, IPhoneNumber[]>()

   phoneNumbers.forEach((phoneNumber) => {
      const areaCode = extractAreaCode(phoneNumber.phoneNumber.phoneNumber)

      if (!groupMap.has(areaCode)) {
         groupMap.set(areaCode, [])
      }

      groupMap.get(areaCode)!.push(phoneNumber)
   })

   // Convert map to array of groups
   const groups: IPhoneNumberGroup[] = Array.from(groupMap.entries()).map(
      ([areaCode, numbers]) => {
         // Use the locality from the first number in the group
         // or the most common locality if you want to be more sophisticated
         const locality = numbers[0]?.phoneNumber.locality || 'Unknown'

         return {
            areaCode,
            locality,
            count: numbers.length,
            numbers,
         }
      }
   )

   // Sort groups by area code
   return groups.sort((a, b) => a.areaCode.localeCompare(b.areaCode))
}

export interface ICampaignWithCount {
   campaignId: string
   numberCount: number
}

export interface ICampaignListItem {
   campaignName: string
   campaignId: string
   numberCount: number
}

/**
 * Gets unique campaign IDs from an array of phone numbers with their associated phone number counts
 * Returns a list of campaigns with the count of phone numbers assigned to each
 */
export const getUniqueCampaignIds = (
   phoneNumbers: IPhoneNumber[]
): ICampaignWithCount[] => {
   const campaignCountMap = new Map<string, number>()

   phoneNumbers.forEach((phoneNumber) => {
      phoneNumber.campaignIds?.forEach((campaignId) => {
         if (campaignId) {
            const currentCount = campaignCountMap.get(campaignId) || 0
            campaignCountMap.set(campaignId, currentCount + 1)
         }
      })
   })

   return Array.from(campaignCountMap.entries()).map(
      ([campaignId, numberCount]) => ({
         campaignId,
         numberCount,
      })
   )
}

export const getNumbersGroupedByCampaignId = (
   phoneNumbers: IPhoneNumber[],
   campaignId: string
): IPhoneNumberGroup[] => {
   const numbers = phoneNumbers.filter((phoneNumber) =>
      phoneNumber.campaignIds?.includes(campaignId)
   )

   return getGroupedPhoneNumbers(numbers as IPhoneNumber[])
}

/**
 * Filters phone number groups based on a search query
 * Searches by area code, locality, phone number, and friendly name
 * Returns filtered groups with updated counts
 */
export const filterPhoneNumberGroups = (
   groups: IPhoneNumberGroup[],
   searchQuery: string
): IPhoneNumberGroup[] => {
   if (!searchQuery.trim()) return groups

   const query = searchQuery.toLowerCase().trim()

   const filtered = groups
      .map((group) => {
         // Check if area code matches
         if (group.areaCode.includes(query)) {
            return group
         }

         // Check if locality matches
         if (group.locality.toLowerCase().includes(query)) {
            return group
         }

         // Filter individual numbers within the group
         const filteredNumbers = group.numbers.filter(
            (number) =>
               number.phoneNumber.phoneNumber.includes(query) ||
               number.phoneNumber.locality?.toLowerCase().includes(query) ||
               number.phoneNumber.friendlyName?.toLowerCase().includes(query) ||
               extractAreaCode(number.phoneNumber.phoneNumber).includes(query)
         )

         if (filteredNumbers.length > 0) {
            return {
               ...group,
               numbers: filteredNumbers,
               count: filteredNumbers.length,
            }
         }

         return null
      })
      .filter((group): group is IPhoneNumberGroup => group !== null)

   return filtered
}

/**
 * Combines campaign data with phone number counts to create a unified campaign list
 * @param campaigns - Array of campaigns from the API with id, campaignName, and status
 * @param phoneNumbers - Array of phone numbers to count assignments
 * @param filterEmpty - If true, excludes campaigns with 0 numbers. Default: true
 * @returns Array of campaigns with their phone number counts
 */
export const getCampaignListWithCounts = (
   campaigns: Array<{ id: string; campaignName: string; status: string }>,
   phoneNumbers: IPhoneNumber[],
   filterEmpty: boolean = true
): ICampaignListItem[] => {
   if (!campaigns || campaigns.length === 0) {
      return []
   }

   // Create a map of campaign ID to phone number count
   const campaignCountMap = new Map<string, number>()

   phoneNumbers.forEach((phoneNumber) => {
      phoneNumber.campaignIds?.forEach((campaignId) => {
         if (campaignId) {
            const currentCount = campaignCountMap.get(campaignId) || 0
            campaignCountMap.set(campaignId, currentCount + 1)
         }
      })
   })

   // Map campaigns to the desired format with counts
   const campaignList = campaigns.map((campaign) => ({
      campaignName: campaign.campaignName,
      campaignId: campaign.id,
      numberCount: campaignCountMap.get(campaign.id) || 0,
   }))

   // Optionally filter out campaigns with 0 numbers
   return filterEmpty
      ? campaignList.filter((campaign) => campaign.numberCount > 0)
      : campaignList
}
